import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';

import {
  Card, BlockStack, InlineStack, InlineGrid, Text, Button, Select,
  IndexTable, Badge, Banner, Modal, Checkbox, Box,
} from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';

import { batchesIndex, batchesStats, batchPdf, batchesPdf } from '../../redux/actions/batches';

const STATUSES = ['prepared', 'picking', 'picking_parked', 'picked', 'packing', 'packing_parked', 'packed_with_returns', 'to_return', 'returning', 'returning_parked', 'complete'];
const OPEN_STATUSES = ['prepared', 'picking', 'picking_parked', 'picked', 'packing', 'packing_parked'];

const STATUS_LABELS = {
  prepared: 'Prepared',
  picking: 'Picking',
  picking_parked: 'Picking (Parked)',
  picked: 'Picked',
  packing: 'Packing',
  packing_parked: 'Packing (Parked)',
  packed_with_returns: 'Packed - Returns',
  to_return: 'To Return',
  returning: 'Returning',
  returning_parked: 'Returning (Parked)',
  complete: 'Complete'
};

const PDF_BATCH_OPTIONS = [
  ['include_status_history', 'Status history'],
  ['include_assignment', 'Assignment'],
];

const PDF_SHIPMENT_OPTIONS = [
  ['include_shipment_status', 'Status'],
  ['include_shipping_method', 'Shipping method'],
  ['include_order_comments', 'Order comments'],
  ['include_staff_notes', 'Staff notes'],
  ['include_shipping_address', 'Shipping Address'],
  ['include_items', 'Items'],
];

const defaultPdfOptions = (includeOrderShipments) => ({
  include_order_shipments: includeOrderShipments,
  include_status_history: true,
  include_assignment: true,
  include_shipment_status: true,
  include_shipping_method: true,
  include_order_comments: true,
  include_staff_notes: false,
  include_shipping_address: true,
  include_items: true,
});

const STATUS_BADGE_TONES = {
  prepared: 'info',
  picking: 'warning',
  picking_parked: 'warning',
  picked: 'info',
  packing: 'warning',
  packing_parked: 'warning',
  packed_with_returns: 'warning',
  to_return: 'info',
  returning: 'info',
  returning_parked: 'warning',
  complete: 'success'
};

const Batches = ({ batches, loadBatches, loadStats, downloadBatchPdf, downloadBatchesPdf }) => {
  const [statusFilter, setStatusFilter] = useState('prepared');
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [pdfDialog, setPdfDialog] = useState(null);
  const [pdfOptions, setPdfOptions] = useState(null);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadBatches(statusFilter === 'all' ? null : statusFilter);
  }, [statusFilter, loadBatches]);

  // The PDF can take a few seconds to generate, so open a blank tab from the
  // click handler and point it at the presigned URL once it is ready - a late
  // window.open would be caught by popup blockers.
  const openPdf = (request, key) => {
    setPdfError(null);
    setDownloadingPdf(key);

    const tab = window.open('', '_blank');

    request
      .then((data) => {
        if (data && data.download_url) {
          if (tab) {
            tab.location.href = data.download_url;
          } else {
            window.open(data.download_url, '_blank');
          }
        } else if (tab) {
          tab.close();
        }
      })
      .catch((error) => {
        if (tab) tab.close();
        setPdfError(error.message || 'Could not download the PDF.');
      })
      .finally(() => setDownloadingPdf(null));
  };

  // 'prepared' means the combined prepared-batches PDF - its order shipment
  // pages default off, while a single batch keeps the full document.
  const openPdfDialog = (key) => {
    setPdfOptions(defaultPdfOptions(key !== 'prepared'));
    setPdfDialog(key);
  };

  const togglePdfOption = (key) => {
    setPdfOptions((options) => ({ ...options, [key]: !options[key] }));
  };

  const confirmPdfDownload = () => {
    const key = pdfDialog;
    setPdfDialog(null);

    const request = key === 'prepared'
      ? downloadBatchesPdf('prepared', pdfOptions)
      : downloadBatchPdf(key, pdfOptions);

    openPdf(request, key);
  };

  const downloadBatch = (batchId) => openPdfDialog(batchId);
  const downloadPrepared = () => openPdfDialog('prepared');

  const refresh = () => {
    loadStats();
    loadBatches(statusFilter === 'all' ? null : statusFilter);
  }

  const statCard = (status) => {
    const count = batches.statusCounts ? batches.statusCounts[status] || 0 : '-';

    return (
      <Box
        key={status}
        background="bg-surface-secondary"
        borderColor="border"
        borderWidth="025"
        borderRadius="200"
        padding="300"
      >
        <BlockStack gap="100" align="center">
          <Text as="h3" variant="headingLg" alignment="center">{count}</Text>
          <Text as="p" variant="bodySm" alignment="center">{STATUS_LABELS[status]}</Text>
        </BlockStack>
      </Box>
    );
  }

  return (
    <SettingsPage
      title="Active Batches"
      description="Have questions or need some help? Contact us at support@athousandapps.com"
    >
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Open Batches</Text>
          <Text as="p" variant="bodyMd">Batches that have not finished packing.</Text>

          <InlineGrid columns={{ xs: 2, sm: 3, md: 6 }} gap="300">
            {OPEN_STATUSES.map(statCard)}
          </InlineGrid>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">Batches</Text>

          <InlineStack gap="300" blockAlign="center" wrap>
            <div style={{ minWidth: '200px' }}>
              <Select
                label="Status"
                labelHidden
                options={['all', ...STATUSES].map((status) => ({
                  label: status === 'all' ? 'All Batches' : STATUS_LABELS[status],
                  value: status
                }))}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            </div>
            { (statusFilter === 'all' || statusFilter === 'prepared') &&
              <Button
                onClick={downloadPrepared}
                loading={downloadingPdf === 'prepared'}
                disabled={Boolean(batches.statusCounts && !batches.statusCounts.prepared)}
              >
                Download Prepared Batches PDF
              </Button>
            }
            <Button
              variant="plain"
              icon={RefreshIcon}
              loading={batches.isFetching}
              onClick={refresh}
            >
              Refresh
            </Button>
          </InlineStack>

          { pdfError &&
            <Banner
              title="PDF download failed"
              tone="critical"
              onDismiss={() => setPdfError(null)}
            >
              <p>{pdfError}</p>
            </Banner>
          }

          { batches.errors &&
            <Banner
              title="Batch request failed"
              tone="critical"
            >
              <p>{batches.errors}</p>
            </Banner>
          }

          { batches.status === 'success' && batches.batches.length > 0 &&
            <IndexTable
              resourceName={{ singular: 'batch', plural: 'batches' }}
              itemCount={batches.batches.length}
              selectable={false}
              headings={[
                { title: 'Batch' },
                { title: 'Status' },
                { title: 'Totes' },
                { title: 'Items' },
                { title: 'Assigned To' },
                { title: 'Created' },
                { title: 'Due' },
                { title: 'PDF' },
              ]}
            >
              {batches.batches.map((batch, index) => (
                <IndexTable.Row id={batch.id} key={batch.id} position={index}>
                  <IndexTable.Cell>
                    <Link to={`/batches/${batch.id}`}>{batch.name || batch.id}</Link>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Badge tone={STATUS_BADGE_TONES[batch.status] || 'info'}>{STATUS_LABELS[batch.status] || batch.status}</Badge>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{batch.tote_count}</IndexTable.Cell>
                  <IndexTable.Cell>{batch.item_count}</IndexTable.Cell>
                  <IndexTable.Cell>{batch.assigned_user_email || '-'}</IndexTable.Cell>
                  <IndexTable.Cell>{batch.created_at ? new Date(batch.created_at).toLocaleString() : '-'}</IndexTable.Cell>
                  <IndexTable.Cell>{batch.due_date ? new Date(batch.due_date).toLocaleString() : '-'}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <Button variant="plain" loading={downloadingPdf === batch.id} onClick={() => downloadBatch(batch.id)}>Download</Button>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          }

          { batches.isFetching &&
            <LoadingSpinner size="small" statusText="Loading batches"/>
          }

          { batches.status === 'success' && !batches.isFetching && batches.batches.length === 0 &&
            <Banner tone="warning">
              <p>No {statusFilter === 'all' ? '' : STATUS_LABELS[statusFilter].toLowerCase() + ' '}batches found. Assign totes to batches from the order shipments page.</p>
            </Banner>
          }
        </BlockStack>
      </Card>

      <Modal
        open={pdfDialog !== null}
        onClose={() => setPdfDialog(null)}
        title="PDF Download Options"
        primaryAction={{
          content: 'Download PDF',
          onAction: confirmPdfDownload
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setPdfDialog(null),
          },
        ]}
      >
        <Modal.Section>
          { pdfOptions &&
            <BlockStack gap="400">
              <Checkbox
                label="Include Order Shipment Information"
                checked={pdfOptions.include_order_shipments}
                onChange={() => togglePdfOption('include_order_shipments')}
              />

              <Text as="h3" variant="headingSm">Batch page</Text>
              <BlockStack gap="200">
                { PDF_BATCH_OPTIONS.map(([key, label]) =>
                  <Checkbox
                    key={key}
                    checked={pdfOptions[key]}
                    onChange={() => togglePdfOption(key)}
                    label={label}
                  />
                )}
              </BlockStack>

              <Text as="h3" variant="headingSm">Order shipment pages</Text>
              <BlockStack gap="200">
                { PDF_SHIPMENT_OPTIONS.map(([key, label]) =>
                  <Checkbox
                    key={key}
                    checked={pdfOptions[key]}
                    disabled={!pdfOptions.include_order_shipments}
                    onChange={() => togglePdfOption(key)}
                    label={label}
                  />
                )}
              </BlockStack>
            </BlockStack>
          }
        </Modal.Section>
      </Modal>
    </SettingsPage>
  )
}

const mapStateToProps = state => ({
  batches: state.batches,
});

const mapDispatchToProps = (dispatch) => ({
  loadBatches: (status) => dispatch(batchesIndex(status)),
  loadStats: () => dispatch(batchesStats()),
  downloadBatchPdf: (batchId, options) => dispatch(batchPdf(batchId, options)),
  downloadBatchesPdf: (status, options) => dispatch(batchesPdf(status, options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Batches);
